import React from 'react';
import { styled } from '@mui/material/styles';
import ResourceTable, { IColumn } from '../../components/ResourceTable';

import {
  CompanyScoringState,
  WomeApplicationDto,
  WomeApplicationUpdateDto,
  WomeStatusDto,
} from 'models/dtoTypes';
import Notice from '../../components/Notice';
import Link from 'components/Link';
import { useWomeApplicationFilterSchema } from './_womeApplicationSchema';
import {
  CreateWomeApplication,
  DeleteWomeApplication,
  GetManyWomeApplicationsAndCount,
  RejectWomeApplication,
  ApproveWomeApplication,
} from 'services/womeApplication';
import { Box, Button, Chip } from '@mui/material';
import generateNonce from 'utils/generateNonce';
import { utc2str } from 'utils/date';
import { getKosmosReportLink } from 'utils/report';
import AddIcon from '@mui/icons-material/Add';
import { useHistory } from 'react-router';
import { CurrentUser } from 'auth';
import CreateResourceModal, { ISchema } from 'kosmos/dialogs/CreateResourceModal';
import { useTranslation } from 'react-i18next';

const PREFIX = 'WomeApplicationListPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    },
  },
}));

interface INoticeData {
  message: string;
  severity: 'error' | 'success';
}

export default function WomeApplicationListPage(): JSX.Element {
  const [noticeData, setNoticeData] = React.useState<INoticeData>();
  const [openApproveId, setOpenApproveId] = React.useState('');
  const [openRejectId, setOpenRejectId] = React.useState('');
  const history = useHistory();
  const [nonce, setNonce] = React.useState('');
  const handleCloseNotice = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setNoticeData(undefined);
  };

  const { t } = useTranslation('default');

  const handleDeleteWomeApplication = async (model: WomeApplicationDto) => {
    try {
      await DeleteWomeApplication(model.id || '');
      setNonce(generateNonce());
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: 'Cannot delete organisation wome application',
        severity: 'error',
      });
    }
  };

  const handleApproveApplication = async (id: string, updateDto?: WomeApplicationUpdateDto) => {
    try {
      const confirmed = confirm(
        'Approve will trigger scoring process and consume one credit for this organisation, are you sure?',
      );
      if (confirmed) {
        await ApproveWomeApplication(id, updateDto);
        setNonce(generateNonce());
        setOpenApproveId('');
      } else {
        return;
      }
    } catch (e) {
      console.error(e);
      const errorMsg = e instanceof Error ? e.message : 'Cannot approve this wome application';
      setNoticeData({
        message: errorMsg,
        severity: 'error',
      });
    }
  };

  const handleRejectApplication = async (id: string, updateDto: WomeApplicationUpdateDto) => {
    try {
      const confirmed = confirm('Reject this application, are you sure?');
      if (confirmed) {
        await RejectWomeApplication(id, updateDto);
        setNonce(generateNonce());
        setOpenRejectId('');
      } else {
        return;
      }
    } catch (e) {
      console.error(e);
      setNoticeData({
        message: 'Cannot reject this wome application',
        severity: 'error',
      });
    }
  };

  const handleEditApplication = (wome: WomeApplicationDto) => {
    history.push(`/wome_companies/${wome.womeCompanyId}/questionnaire/wome-company-information`);
  };

  const generateColumns = (): IColumn<WomeApplicationDto>[] => [
    {
      label: t('organisation'),
      dataIndex: 'organisationId',
      sortIndex: 'organisationId',
      render: (model: WomeApplicationDto) => {
        return (
          <div>
            <div>
              <Link
                to={`/organisations/${model.organisationId || ''}`}
                text={model.organisation?.name || '(Unknown)'}
              />
            </div>
            <div>{model.createByUser?.email || ''}</div>
          </div>
        );
      },
    },
    {
      label: t('name'),
      dataIndex: 'womeCompanyId',
      sortIndex: 'womeCompanyId',
      render: (model: WomeApplicationDto) => {
        const companyDisplayName = model.womeCompany?.name || model.womeCompany?.id || '(Unknown)';
        if (model.womeCompany?.scoringState === CompanyScoringState.Completed) {
          return (
            <Link
              to={getKosmosReportLink(model.womeCompanyId, model.organisationId)}
              text={companyDisplayName}
            />
          );
        } else {
          return companyDisplayName;
        }
      },
    },
    {
      label: t('score'),
      dataIndex: '',
      sortIndex: '',
      render: (model: WomeApplicationDto) => {
        const esgScore = model.womeCompany?.esgScore || 0;
        if (model.womeCompany?.scoringState === CompanyScoringState.Completed && esgScore) {
          return (
            <Link
              to={getKosmosReportLink(model.womeCompanyId, model.organisationId)}
              text={`${esgScore}`}
            />
          );
        } else {
          return model.womeCompany?.scoringState || '';
        }
      },
    },
    {
      label: t('reviewer-status'),
      dataIndex: 'womeStatus',
      sortIndex: 'womeStatus',
      render: (model: WomeApplicationDto) => {
        switch (model.womeStatus) {
          case WomeStatusDto.Pending:
            return <Chip label="Pending" color="info" />;
          case WomeStatusDto.WaitingReview:
            return <Chip label="Under Review" color="primary" />;
          case WomeStatusDto.Rejected:
            return <Chip label="Rejected" color="warning" />;
          case WomeStatusDto.Approved:
            return <Chip label="Approved" color="success" />;
          default:
            return '';
        }
      },
    },
    {
      label: t('reviewer-note'),
      dataIndex: 'adminNote',
      sortIndex: 'adminNote',
      render: (model: WomeApplicationDto) => {
        return (
          <Box style={{ minWidth: 200 }}>
            <div>{model.acceptedByUser && <div>{model.acceptedByUser?.email || ''}</div>}</div>
            {model.adminNote && (
              <pre style={{ backgroundColor: '#F1F1F1', padding: 10 }}>{model.adminNote || ''}</pre>
            )}
          </Box>
        );
      },
    },
    {
      label: t('created-at'),
      dataIndex: 'created',
      sortIndex: 'created',
      render: (model: WomeApplicationDto) => {
        if (model?.created) {
          return utc2str(model?.created, 'YYYY-MM-DD HH:mm:ss');
        } else {
          return <></>;
        }
      },
    },
    {
      label: t('action'),
      actions: [
        {
          label: t('edit'),
          onClick: handleEditApplication,
        },
        {
          label: t('approve'),
          hide: (model) =>
            !CurrentUser.getInstance().isAdmin() || model.womeStatus === WomeStatusDto.Approved,
          onClick: (model) => setOpenApproveId(model.id || ''),
        },
        {
          label: t('rescore'),
          hide: (model) =>
            !CurrentUser.getInstance().isAdmin() || model.womeStatus !== WomeStatusDto.Approved,
          onClick: (model) => handleApproveApplication(model.id || ''),
        },
        {
          label: t('reject'),
          hide: () => !CurrentUser.getInstance().isAdmin(),
          onClick: (model) => setOpenRejectId(model.id || ''),
        },
        {
          label: t('remove'),
          onClick: handleDeleteWomeApplication,
        },
      ],
    },
  ];

  const handleCreateWomeApplication = async () => {
    const wome = await CreateWomeApplication();
    history.push(`/wome_companies/${wome.womeCompanyId}/questionnaire/wome-company-information`);
  };

  const schema: ISchema = {
    fields: [
      {
        label: t('note'),
        name: 'adminNote',
        required: true,
        widget: 'textArea',
        description: '',
      },
    ],
  };

  return (
    <Root>
      <div className={classes.pageHeader}>
        <h2>{t('company-registrations')}</h2>
        <Button onClick={handleCreateWomeApplication} variant="contained" startIcon={<AddIcon />}>
          {t('create')}
        </Button>
      </div>

      <ResourceTable
        key={nonce}
        filterSchema={useWomeApplicationFilterSchema()}
        columns={generateColumns()}
        onFetch={GetManyWomeApplicationsAndCount}
        defaultSort={{
          sortColumn: 'created',
          isDesc: false,
        }}
      />
      <CreateResourceModal<WomeApplicationUpdateDto>
        open={Boolean(openApproveId)}
        label={t('approve')}
        schema={schema}
        onClose={() => setOpenApproveId('')}
        submitText={t('approve')}
        onSave={(updateDto) => handleApproveApplication(openApproveId, updateDto)}
      />
      <CreateResourceModal<WomeApplicationUpdateDto>
        open={Boolean(openRejectId)}
        label={t('reject')}
        schema={schema}
        onClose={() => setOpenRejectId('')}
        submitText={t('reject')}
        onSave={(updateDto) => handleRejectApplication(openRejectId, updateDto)}
      />
      {noticeData && (
        <Notice
          open={true}
          message={noticeData.message}
          severity={noticeData.severity}
          onClose={handleCloseNotice}
        />
      )}
    </Root>
  );
}
